import { onBeforeUnmount, ref } from 'vue';
import { apiUrl } from './api-url';
import { markNetworkRequestFailed, markNetworkRequestSucceeded } from './connectivity';
import { coalesceRead,clearAssetCache } from './read-work';

export const authRequired = ref(false);
export const activeDashboardUserId = ref(localStorage.getItem('eqover-dashboard-user') ?? '');
export const impersonationActive = ref(localStorage.getItem('eqover-impersonation-active') === '1');

const tokenKey = 'eqover-token';
const tokenExpiryKey = 'eqover-token-expires-at';
const webAppTokenKey = 'eqover-webapp-token';
const webAppTokenExpiryKey = 'eqover-webapp-token-expires-at';
const administratorTokenBackupKey = 'eqover-administrator-token-backup';
const administratorTokenExpiryBackupKey = 'eqover-administrator-token-expires-at-backup';
const administratorWebAppTokenBackupKey = 'eqover-administrator-webapp-token-backup';
const administratorWebAppTokenExpiryBackupKey = 'eqover-administrator-webapp-token-expires-at-backup';
const impersonationMarkerKey = 'eqover-impersonation-active';

migrateLegacyWebAppToken();

export function getApiToken(): string | null {
  const persistentToken = storedToken(tokenKey, tokenExpiryKey);
  if (persistentToken) return persistentToken;

  // Preserve tokens entered before they became persistent.
  const sessionToken = sessionStorage.getItem(tokenKey);
  if (sessionToken) {
    localStorage.setItem(tokenKey, sessionToken);
    sessionStorage.removeItem(tokenKey);
  }
  return sessionToken;
}

function storedToken(tokenKey: string, expiryKey: string): string | null {
  const token = localStorage.getItem(tokenKey);
  if (!token) return null;
  const expiresAt = localStorage.getItem(expiryKey);
  if (expiresAt && new Date(expiresAt).getTime() <= Date.now()) {
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(expiryKey);
    return null;
  }
  return token;
}

export function setApiToken(token: string, expiresAt?: string): void {
  localStorage.setItem(tokenKey, token);
  if (expiresAt) localStorage.setItem(tokenExpiryKey, expiresAt);
  else localStorage.removeItem(tokenExpiryKey);
  removeLegacyWebAppToken();
  sessionStorage.removeItem(tokenKey);
  authRequired.value = false;
  window.dispatchEvent(new CustomEvent('eqover:api-token-changed'));
}

export function clearApiToken(): void {
  localStorage.removeItem(tokenKey);
  localStorage.removeItem(tokenExpiryKey);
  removeLegacyWebAppToken();
  sessionStorage.removeItem(tokenKey);
  setActiveDashboardUser('');
  clearImpersonationBackup();
  authRequired.value = true;
  window.dispatchEvent(new CustomEvent('eqover:api-token-changed'));
}

export function beginImpersonation(accessToken: string, expiresAt: string): void {
  const administratorToken = storedToken(tokenKey, tokenExpiryKey);
  if (!administratorToken) throw new Error('No se encontró la sesión de administrador para poder restaurarla.');
  localStorage.setItem(administratorTokenBackupKey, administratorToken);
  const administratorExpiresAt = localStorage.getItem(tokenExpiryKey);
  if (administratorExpiresAt) localStorage.setItem(administratorTokenExpiryBackupKey, administratorExpiresAt);
  else localStorage.removeItem(administratorTokenExpiryBackupKey);
  localStorage.setItem(impersonationMarkerKey, '1');
  impersonationActive.value = true;
  setApiToken(accessToken, expiresAt);
}

export function restoreAdministratorSession(): boolean {
  const administratorToken = storedToken(administratorTokenBackupKey, administratorTokenExpiryBackupKey);
  if (!administratorToken) {
    clearApiToken();
    return false;
  }
  const expiresAt = localStorage.getItem(administratorTokenExpiryBackupKey) ?? undefined;
  localStorage.removeItem(administratorTokenBackupKey);
  localStorage.removeItem(administratorTokenExpiryBackupKey);
  localStorage.removeItem(administratorWebAppTokenBackupKey);
  localStorage.removeItem(administratorWebAppTokenExpiryBackupKey);
  localStorage.removeItem(impersonationMarkerKey);
  impersonationActive.value = false;
  setApiToken(administratorToken, expiresAt);
  return true;
}

function clearImpersonationBackup(): void {
  removeLegacyWebAppToken();
  localStorage.removeItem(administratorTokenBackupKey);
  localStorage.removeItem(administratorTokenExpiryBackupKey);
  localStorage.removeItem(administratorWebAppTokenBackupKey);
  localStorage.removeItem(administratorWebAppTokenExpiryBackupKey);
  localStorage.removeItem(impersonationMarkerKey);
  impersonationActive.value = false;
}

function migrateLegacyWebAppToken(): void {
  if (!storedToken(tokenKey, tokenExpiryKey)) {
    const legacyToken = storedToken(webAppTokenKey, webAppTokenExpiryKey);
    if (legacyToken) {
      localStorage.setItem(tokenKey, legacyToken);
      const legacyExpiry = localStorage.getItem(webAppTokenExpiryKey);
      if (legacyExpiry) localStorage.setItem(tokenExpiryKey, legacyExpiry);
    }
  }
  removeLegacyWebAppToken();
}

function removeLegacyWebAppToken(): void {
  localStorage.removeItem(webAppTokenKey);
  localStorage.removeItem(webAppTokenExpiryKey);
}

export async function revokeApiToken(accessToken: string | null): Promise<void> {
  if (!accessToken) return;
  await fetch(apiUrl('/api/auth/logout'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: '{}',
  });
}

export function setActiveDashboardUser(id: string): void {
  activeDashboardUserId.value = id;
  if (id) localStorage.setItem('eqover-dashboard-user', id);
  else localStorage.removeItem('eqover-dashboard-user');
}

export interface ApiEnvelope<T> {
  data: T;
  meta: { demo: boolean; generatedAt: string };
}

export async function apiGet<T>(path: string): Promise<ApiEnvelope<T>> {
  return coalesceRead(JSON.stringify([getApiToken(),activeDashboardUserId.value,path]),()=>request<T>(path,{method:'GET'}));
}

export async function apiPost<T>(path: string, body: unknown): Promise<ApiEnvelope<T>> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export async function apiPatch<T>(path: string, body: unknown): Promise<ApiEnvelope<T>> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function apiPutImage<T>(path: string, file: File): Promise<ApiEnvelope<T>> {
  return apiPutFile<T>(path, file);
}

export async function apiPutFile<T>(path: string, file: Blob): Promise<ApiEnvelope<T>> {
  const token = getApiToken();
  const response = await fetch(apiUrl(path), {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: file,
  });
  const payload = await response.json() as ApiEnvelope<T> | { error: string };
  if (response.status === 401 && getApiToken()===token) clearApiToken();
  if (!response.ok || 'error' in payload) throw new Error('error' in payload ? payload.error : `HTTP ${response.status}`);
  if (token && getApiToken()===token) authRequired.value = false;
  clearAssetCache();
  window.dispatchEvent(new Event('eqover:assets-changed'));
  return payload;
}

export async function apiDelete<T>(path: string): Promise<ApiEnvelope<T>> {
  return request<T>(path, { method: 'DELETE' });
}

export async function fetchApiFile(file: { url?: string | null; contentType?: string | null }, signal?: AbortSignal): Promise<Blob> {
  if (!file.url) throw new Error('El archivo adjunto no tiene una URL disponible.');
  const token = getApiToken();
  const response = await fetch(apiUrl(file.url), {
    signal,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (response.status === 401 && getApiToken() === token) clearApiToken();
  if (!response.ok) {
    let message = `No se pudo abrir el archivo (HTTP ${response.status}).`;
    try {
      const payload = await response.json() as { error?: string };
      if (payload.error) message = payload.error;
    } catch { /* la respuesta puede no ser JSON */ }
    throw new Error(message);
  }
  return new Blob([await response.arrayBuffer()], {
    type: response.headers.get('content-type') || file.contentType || 'application/octet-stream',
  });
}

export async function openApiFile(file: { url?: string | null; filename?: string | null; contentType?: string | null }): Promise<void> {
  if (!file.url) throw new Error('El archivo adjunto no tiene una URL disponible.');
  const contentType = String(file.contentType ?? '').toLowerCase();
  const openInline = contentType === 'application/pdf' || contentType.startsWith('image/') || contentType.startsWith('video/')
    || /\.(?:heic|heif)$/i.test(file.filename ?? '') || contentType.startsWith('text/');
  const tab = openInline ? window.open('about:blank', '_blank') : null;
  if (tab) tab.opener = null;
  try {
    const objectUrl = URL.createObjectURL(await fetchApiFile(file));
    if (openInline) {
      if (tab) tab.location.replace(objectUrl);
      else window.open(objectUrl, '_blank', 'noopener,noreferrer');
    } else {
      const download = document.createElement('a');
      download.href = objectUrl;
      download.download = file.filename || 'adjunto';
      download.rel = 'noopener';
      document.body.appendChild(download);
      download.click();
      download.remove();
    }
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 5 * 60_000);
  } catch (error) {
    tab?.close();
    throw error;
  }
}

export async function downloadApiFile(path: string, fallbackFilename: string): Promise<void> {
  const token = getApiToken();
  const response = await fetch(apiUrl(path), {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (response.status === 401) clearApiToken();
  if (!response.ok) {
    let message = `No se pudo descargar el archivo (HTTP ${response.status}).`;
    try {
      const payload = await response.json() as { error?: string };
      if (payload.error) message = payload.error;
    } catch { /* la respuesta puede no ser JSON */ }
    throw new Error(message);
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const encodedFilename = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  let headerFilename = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  if (encodedFilename) {
    try { headerFilename = decodeURIComponent(encodedFilename.replace(/^"|"$/g, '')); }
    catch { /* conserva el nombre alternativo si el encabezado no es válido */ }
  }
  const objectUrl = URL.createObjectURL(await response.blob());
  const download = document.createElement('a');
  download.href = objectUrl;
  download.download = headerFilename || fallbackFilename;
  download.rel = 'noopener';
  document.body.appendChild(download);
  download.click();
  download.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

async function request<T>(path: string, init: RequestInit): Promise<ApiEnvelope<T>> {
  const token = getApiToken();
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      ...init,
      headers: {
        ...(init.body != null ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
    markNetworkRequestSucceeded();
  } catch (cause) {
    markNetworkRequestFailed(cause);
    throw cause;
  }
  const payload = await response.json() as ApiEnvelope<T> | { error: string };
  if (response.status === 401 && getApiToken()===token) clearApiToken();
  if (!response.ok || 'error' in payload) throw new Error('error' in payload ? payload.error : `HTTP ${response.status}`);
  if (token&&getApiToken()===token) authRequired.value = false;
  return payload;
}

export function useLiveEvents(onEvent?: (event: Record<string, unknown>) => void) {
  const connected = ref(false);
  const lastEvent = ref<Record<string, unknown> | null>(null);
  const abort = new AbortController();
  const connect = async () => {
    const token = getApiToken();
    try {
      const response = await fetch(apiUrl('/api/events'), {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: abort.signal,
      });
      if (!response.ok || !response.body) throw new Error('No se pudo abrir el canal en vivo');
      connected.value = true;
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split('\n\n');
        buffer = blocks.pop() ?? '';
        for (const block of blocks) {
          const data = block.split('\n').find((line) => line.startsWith('data: '))?.slice(6);
          if (!data) continue;
          try { lastEvent.value = JSON.parse(data); onEvent?.(lastEvent.value!); } catch { /* keep stream alive */ }
        }
      }
    } catch {
      connected.value = false;
    }
  };
  void connect();
  onBeforeUnmount(() => abort.abort());
  return { connected, lastEvent };
}
